import React, { Fragment, useCallback } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Button, Grid, Paper, Typography } from '@material-ui/core';

import { ArchivalUnitCopy, EadNode, EvidenceUnit } from 'types';
import { ReadOnlyField } from 'components/read-only-field/read-only-field';
import { RegisterField } from 'modules/register/register-field';
import {
  decideRelationLabel,
  handleOriginatorLink,
} from 'modules/register/reqister-utils';
import { StructMap } from 'components/struct-map/struct-map';
import {
  LinkField,
  MultiLinkFieldRouter,
} from 'components/link-field/link-field';
import { useIntl } from 'react-intl';
import { AccessibleDigitalContent } from 'modules/archival-unit-private/archival-unit-detail';
import { EvidenceUnitLabels, getSpaceOccupiedLabel } from 'enums';

import CollapseSection from 'components/collapse-section';
import {
  LevelDefaultIcon,
  levelIcon,
} from 'components/grid/grid-hooks/breadcrumbs-hook';
import { NavToParentField } from 'components/grid/grid-hooks/item-hierarchy-context-hook';
import {
  constructEadMapLink,
  constructMapLinkLabel,
} from 'modules/map/mapUtils';
import { isDevelopment } from 'utils';
import { constructOriginatorLabel } from 'modules/register/utils/construct-originator-labels';
import { useCollapseSections } from 'components/collapse-section/collapse-section-hook';

const capitalize = (val: string) => val.charAt(0).toUpperCase() + val.slice(1);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
    },
    paper: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
    },
    archivalUnitInfo: {
      marginTop: 10,
      fontWeight: 400,
      color: theme.palette.primary.main,
    },
    content: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
    },
    flexRow: {
      display: 'flex',
      alignItems: 'center',
    },
    icon: {
      marginRight: 8,
    },
  })
);

export function ArchivalUnitDetail({
  item,
  parentEad,
}: {
  item?: ArchivalUnitCopy;
  parentEad?: EadNode;
}) {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const {
    handleCollapseAll,
    handleExpandAll,
    handleStateChange,
    allCollapsed,
    allExpanded,
    isExpanded,
    registerKey,
  } = useCollapseSections();

  const {
    numericId,
    archivalUnitReference,
    summary,
    originator,
    structMap,
    aipData = [],
    zipFile,
  } = item || {};

  const {
    archivalCollection,
    entityCharacter,
    timeRange,
    accessType,
    structuredLink,
    evidenceTypeName,
    storageUnits = [],
    describingArchivalAids = [],
  } = archivalUnitReference || {};

  const archivalAid =
    describingArchivalAids.length > 0 ? describingArchivalAids[0] : undefined;

  const downloadZippedContent = useCallback(async () => {
    window.open(
      process.env.PUBLIC_URL + `/api/file/archival/${zipFile?.id}`,
      '_blank'
    );
  }, [zipFile]);
  const eadNode = parentEad;

  const LevelIcon = eadNode?.level
    ? levelIcon?.[eadNode.level]
    : LevelDefaultIcon;

  return (
    <>
      {isDevelopment() && <>DEV: AU DETAIL</>}
      <Typography variant="h5">
        <div className={classes.flexRow}>
          <LevelIcon fontSize="small" className={classes.icon} />
          {summary} {timeRange}
        </div>
      </Typography>
      {entityCharacter === 'DIGITAL' && aipData.length > 0 && (
        <p className={classes.archivalUnitInfo}>
          Toto je digitální archiválie, její plný obsah je zpřístupněný,
          archviálii nelze objednat.
        </p>
      )}
      {entityCharacter === 'HYBRID' && aipData.length > 0 && (
        <p className={classes.archivalUnitInfo}>
          Objednáváte si digitalizovanou archiválii. Archiválie Vám bude
          zpřístupně jen pokud pro účel Vašeho studia nestačí digitální kopie.
          Nezapomeňte proto do poznámky uvést zdůvodnění.
        </p>
      )}
      <Paper elevation={0} className={classes.paper}>
        <Grid container className={classes.root}>
          <Typography variant="h6">
            {formatMessage({
              id: 'archivalUnit.inclusion',
            })}
          </Typography>
          <br />
          <br />
          <LinkField
            label={formatMessage({
              id: 'archivalUnit.archivalCollection',
            })}
            value={archivalCollection?.name}
            href={`fondy/0/${archivalCollection?.id}`}
          />
          <LinkField
            label={formatMessage({
              id: 'archivalUnit.archivalCollectionNumber',
            })}
            value={archivalCollection?.evidenceNumber}
            href={`fondy/0/${archivalCollection?.id}`}
          />
          {archivalAid && (
            <LinkField
              label={formatMessage({
                id: 'archivalUnit.archivalAid',
              })}
              value={archivalAid?.name}
              href={`pomucky/0/${archivalAid?.id}`}
            />
          )}
          <ReadOnlyField
            label="Úroveň"
            value={parentEad?.levelLabel ?? 'archiválie'}
          />
          <NavToParentField />
        </Grid>
      </Paper>
      <Grid
        container
        className={classes.root}
        style={{ justifyContent: 'flex-end', paddingBottom: 0 }}
      >
        <Button
          disabled={!allCollapsed()}
          onClick={() => {
            handleCollapseAll();
          }}
        >
          Sbalit vše
        </Button>
        <Button
          disabled={!allExpanded()}
          onClick={() => {
            handleExpandAll();
          }}
        >
          Rozbalit vše
        </Button>
      </Grid>
      {/* IDENTIFIKACE */}
      <Paper elevation={0} className={classes.paper}>
        <CollapseSection
          title="Identifikace"
          id="identification"
          overrideOpen={isExpanded('identification')}
          registerKey={registerKey}
          onStateChange={handleStateChange}
        >
          <Grid container className={classes.root}>
            <br />
            <br />
            <ReadOnlyField
              label={formatMessage({
                id: 'archivalUnit.referenceDesignation',
              })}
              value={structuredLink}
            />
            <ReadOnlyField
              label="Číslo archiválie"
              value={numericId?.toString()}
            />
            <ReadOnlyField
              label="Pořadové číslo"
              value={
                eadNode?.referenceLabels?.find(
                  (rl) => rl.type === 'PORADOVE_CISLO'
                )?.value
              }
            />
            {eadNode &&
              eadNode.referenceLabels
                ?.filter(
                  (rl) =>
                    rl.type !== 'PORADOVE_CISLO' &&
                    rl.type !== 'REFERENCNI_OZNACENI'
                )
                ?.map((rl) => (
                  <ReadOnlyField
                    key={rl.type}
                    label={capitalize(rl.label)}
                    value={rl.value}
                  />
                ))}
            <ReadOnlyField
              label={formatMessage({
                id: 'archivalUnit.storageUnit',
              })}
              value={
                storageUnits.length > 0
                  ? storageUnits.map((s) => s).join(', ')
                  : undefined
              }
            />
            <ReadOnlyField
              label={formatMessage({
                id: 'archivalUnit.type',
              })}
              value={evidenceTypeName}
            />
            <ReadOnlyField
              label={formatMessage({ id: 'archivalUnit.form' })}
              value={formatMessage({
                id: `archivalUnit.entityCharacter.${entityCharacter}`,
              })}
            />
          </Grid>
        </CollapseSection>
      </Paper>
      {/* POPIS */}
      <Paper elevation={0} className={classes.paper}>
        <CollapseSection
          title="Popis"
          id="description"
          overrideOpen={isExpanded('description')}
          registerKey={registerKey}
          onStateChange={handleStateChange}
        >
          <Grid container className={classes.root}>
            <ReadOnlyField
              label={formatMessage({
                id: 'archivalUnit.regest',
              })}
              value={summary}
              alwaysShow={true}
            />
            <ReadOnlyField
              label={formatMessage({
                id: 'archivalUnit.dating',
              })}
              value={timeRange}
            />
            <ReadOnlyField
              label="Formální název jednotky popisu"
              value={eadNode?.formalTitle}
            />

            <ReadOnlyField
              label="Textový způsob zápisu datace"
              value={eadNode?.fromTextual}
            />
          </Grid>
        </CollapseSection>
      </Paper>
      {eadNode && (
        <Paper elevation={0} className={classes.paper}>
          <CollapseSection
            title="Rozšířený popis"
            id="extended-description"
            overrideOpen={isExpanded('extended-description')}
            registerKey={registerKey}
            onStateChange={handleStateChange}
          >
            <Grid container className={classes.root}>
              {originator && (
                <RegisterField
                  label={formatMessage({
                    id: 'archivalUnit.originator',
                  })}
                  withoutBorder={true}
                  value={originator?.preferredName?.label}
                  valueLink={
                    originator && handleOriginatorLink(originator, true)
                  }
                />
              )}
              <ReadOnlyField
                label="Dějiny jednotky popisu"
                value={eadNode.custodHist}
              />
              <ReadOnlyField
                label="Způsob uspořádání jednotky popisu"
                value={eadNode.arrangement}
              />
              <ReadOnlyField
                label="Tematický popis jednotky popisu"
                value={eadNode.scopeContent}
              />
              <ReadOnlyField
                label="Přímý zdroj akvizice"
                value={eadNode.acqInfo}
              />
              <ReadOnlyField
                label="Pravidla zpracování"
                value={eadNode?.rules}
              />
              <ReadOnlyField
                label="Budoucí přírůstky"
                value={eadNode.accruals}
              />
              <ReadOnlyField label="Fyzický stav" value={eadNode.physTech} />

              <ReadOnlyField
                label="	Identifikace předlohy kopie"
                value={eadNode?.originalsLoc}
              />
              <ReadOnlyField
                label={formatMessage({ id: 'archivalUnit.digitalization' })}
                Component={
                  <AccessibleDigitalContent
                    hasDigitalContent={aipData.length > 0}
                    structMapData={structMap}
                  />
                }
              />

              <ReadOnlyField
                label={formatMessage({
                  id: 'archivalUnit.accessibility',
                })}
                value={
                  accessType
                    ? formatMessage({
                        id: `archivalUnit.accessType.${accessType}`,
                      })
                    : undefined
                }
              />
              {/* ziadost o odstranenie, bod 11 - https://docs.google.com/document/d/17cZE85bvPm1RgdSPzCWqU2Hb3k2x_DYtM_SnZRYWJec/edit */}
              {/* <ReadOnlyField
                label="Úroveň"
                value={eadNode.levelLabel ?? eadNode.level}
              /> */}

              <ReadOnlyField
                label="Způsob a forma dochování"
                value={eadNode.didPhysDesc}
              />

              <ReadOnlyField
                label="Měřítko"
                value={eadNode.scaleMaterialSpec}
              />

              {parentEad?.coordinates && parentEad.coordinates?.length > 0 && (
                <MultiLinkFieldRouter
                  links={parentEad.coordinates.map((c, i) => ({
                    to: `${constructEadMapLink(parentEad.id, i)}`,
                    ...constructMapLinkLabel(c),
                  }))}
                  label="Pozice na mapě"
                  target="_blank"
                />
              )}

              <ReadOnlyField
                label="Orientace"
                value={eadNode.orientationMaterialSpec}
              />
              <ReadOnlyField label="Část" value={eadNode.volumeMaterialSpec} />
              {eadNode.evidenceUnits?.map((eu, index) => (
                <Fragment key={index}>
                  {Object.keys(eu)
                    .filter(
                      (k) =>
                        EvidenceUnitLabels[k as keyof EvidenceUnit]?.render ??
                        true
                    )
                    .map((k, kindex) => (
                      <ReadOnlyField
                        key={kindex}
                        label={
                          EvidenceUnitLabels?.[k as keyof EvidenceUnit]
                            ?.label ?? k
                        }
                        value={eu?.[k as keyof EvidenceUnit] ?? '??'}
                      />
                    ))}
                </Fragment>
              ))}

              {(eadNode?.spaceOccupied ?? []).map((so, index) => (
                <ReadOnlyField
                  key={`so-${index}-${so.unitKind}`}
                  label={getSpaceOccupiedLabel(so)}
                  value={so.quantity}
                />
              ))}

              <ReadOnlyField label="Kopie" value={eadNode?.altFormAvail} />

              <ReadOnlyField
                label="Odkazy na příbuzné dokumenty"
                value={eadNode?.relatedMaterial}
              />
              <ReadOnlyField
                label="Jazyk"
                value={eadNode?.langMaterial?.join(', ')}
              />
              <ReadOnlyField
                label="Podmínky přístupu"
                value={eadNode?.accessRestrict}
              />
              <ReadOnlyField
                label="Edice a literatura"
                value={eadNode?.bibliography}
              />
              <ReadOnlyField label="Veřejná poznámka" value={eadNode?.note} />
            </Grid>
          </CollapseSection>
        </Paper>
      )}
      {(eadNode?.relations?.length ?? 0) > 0 && eadNode && (
        <Paper elevation={0} className={classes.paper}>
          <CollapseSection
            overrideOpen={isExpanded('register')}
            id="register"
            onStateChange={handleStateChange}
            registerKey={registerKey}
            title="Rejstříková hesla"
          >
            <Grid container className={classes.root}>
              {eadNode.relations?.map((r) => (
                <RegisterField
                  key={r.id}
                  label={decideRelationLabel(r)}
                  value={constructOriginatorLabel(r.originator)}
                  withoutBorder={true}
                  valueLink={
                    r.originator && handleOriginatorLink(r.originator, true)
                  }
                />
              ))}
            </Grid>
          </CollapseSection>
        </Paper>
      )}
      {structMap !== undefined && (
        <Paper elevation={0} className={classes.paper} id="digital-content">
          <Grid container className={classes.root}>
            <div className={classes.content}>
              <Typography variant="h6">
                {formatMessage({ id: 'archivalUnit.content' })}
              </Typography>
              {zipFile && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={downloadZippedContent}
                >
                  {formatMessage({
                    id: 'archivalUnit.downloadAllContent',
                  })}
                </Button>
              )}
            </div>
            <StructMap
              data={structMap}
              onChange={() => {}}
              parentEntity={item!}
            />
          </Grid>
        </Paper>
      )}
    </>
  );
}
